import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getOrder, ordersQueryKeys, type Order } from '@api/orders';
import { ApiError } from '@api/client';

// How often to re-fetch while the AI extraction is still running.
const EXTRACTION_POLL_MS = 2_000;
// Stop polling after ~3 minutes (90 × 2s): if extraction hasn't settled by then
// the job is stuck, and the page should fall back to the failed notice rather
// than spin forever.
const EXTRACTION_POLL_MAX_FETCHES = 90;

// A queued Attio sync normally settles in seconds. Its own budget rather than
// extraction's: SyncDealJob retries a transient failure five times with
// backoff, so a link can legitimately sit pending for ~20 minutes. Polling that
// long would be absurd, so we watch for two minutes and then stop -- showing a
// static label, never an error, because the row is most likely still healthy.
const ATTIO_POLL_MS = 5_000;
const ATTIO_POLL_BUDGET_MS = 120_000;

export interface UseOrderOptions {
  initialData?: Order;
  enabled?: boolean;
}

export function useOrder(id: number, options?: UseOrderOptions) {
  // Wall-clock rather than a fetch count: dataUpdateCount is shared with the
  // extraction poll above, so an order that extracted first would arrive here
  // with the budget already spent.
  //
  // Both are keyed on the order id: the preview sheet keeps this hook mounted
  // while the rep moves between orders, and one pending order must not inherit
  // another's spent budget or timed-out spinner.
  const attioPollClock = useRef<{ orderId: number; startedAt: number } | null>(null);
  const [attioTimedOutOrderId, setAttioTimedOutOrderId] = useState<number | null>(null);
  const attioSyncTimedOut = attioTimedOutOrderId === id;

  const attioPollStartedAt = () => {
    if (attioPollClock.current?.orderId !== id) {
      attioPollClock.current = { orderId: id, startedAt: Date.now() };
    }
    return attioPollClock.current.startedAt;
  };

  const query = useQuery({
    queryKey: ordersQueryKeys.detail(id),
    queryFn: () => getOrder(id),
    initialData: options?.initialData,
    enabled: options?.enabled,
    staleTime: 30_000,
    // Poll while either job is in flight, stopping as soon as it settles or its
    // budget is spent. Extraction first: it blocks the whole page, where a
    // pending Attio sync is one row.
    refetchInterval: (q) => {
      const data = q.state.data;

      if (data?.extraction_status === 'processing') {
        return q.state.dataUpdateCount > EXTRACTION_POLL_MAX_FETCHES ? false : EXTRACTION_POLL_MS;
      }

      if (data?.attio?.state === 'pending') {
        return Date.now() - attioPollStartedAt() < ATTIO_POLL_BUDGET_MS ? ATTIO_POLL_MS : false;
      }

      return false;
    },
    retry: (failureCount, error) => {
      // Don't retry on client errors (4xx) - these won't succeed on retry
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        return false;
      }
      // Retry up to 3 times for network/server errors
      return failureCount < 3;
    },
  });

  const attioPending = query.data?.attio?.state === 'pending';

  // Nothing re-renders when the budget simply runs out -- polling stops, so no
  // fetch settles and the spinner would otherwise keep turning. A timer flips
  // it explicitly. Pressing Retry puts the row back to pending, which resets
  // both the clock and the flag.
  useEffect(() => {
    if (!attioPending) {
      attioPollClock.current = null;
      setAttioTimedOutOrderId(null);
      return;
    }

    const remaining = ATTIO_POLL_BUDGET_MS - (Date.now() - attioPollStartedAt());
    if (remaining <= 0) {
      setAttioTimedOutOrderId(id);
      return;
    }

    const timer = setTimeout(() => setAttioTimedOutOrderId(id), remaining);
    return () => clearTimeout(timer);
  }, [attioPending, id]);

  // Still "processing" after the poll budget → the extraction job is stuck;
  // callers should render the failed state instead of the loader.
  const extractionTimedOut =
    query.data?.extraction_status === 'processing' &&
    query.dataUpdateCount > EXTRACTION_POLL_MAX_FETCHES;

  return { ...query, extractionTimedOut, attioSyncTimedOut };
}
